import dayjs from "dayjs";
import { type GetCoinPropertiesResponse } from "deso-protocol";
import { useParams } from "next/navigation";
import type { ReactNode } from "react";
import { use } from "react";
import { useEffect, useState } from "react";
import { LuCheck, LuX } from "react-icons/lu";

import CoinPriceFormatter from "@/components/shared/coin-price-formatter";
import CopyToClipboard from "@/components/shared/copy-to-clipboard";
import PricePerCoinTooltip from "@/components/shared/creator-coin-exchange-modal/price-per-coin-tooltip";
import { InfoTooltip } from "@/components/shared/info-tooltip";
import { TimestampTooltip } from "@/components/shared/timestamp-tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { useFocusMarketData } from "@/contexts/focus-market-data";
import { UserContext } from "@/contexts/user-context";
import { useCoinPropertiesQuery } from "@/hooks/use-coin-properties-query";
import { useCreatorCoinFee } from "@/hooks/use-creator-coin-fee";
import { useResolveUsername } from "@/hooks/use-resolve-username";
import { useTokenMarketData } from "@/hooks/use-token-market-data";
import {
  APP_NAME_GENERIC,
  DESO_APP_NAME,
  FOCUS_TOKEN_DISPLAY_NAME,
  FOCUS_TOKEN_SUPPLY,
} from "@/utils/constants";
import {
  basisPointsToPercent,
  dateNanosToInteger,
  formatDecimalValue,
  formatUSD,
  formatUSDCents,
  getVisiblePrice,
  tokenNanosToInteger,
  usdToFocus,
} from "@/utils/currency";
import { FocusDateParser } from "@/utils/date";
import { formatDateTime, pluralize, shortenLongWord } from "@/utils/helpers";
import { cn } from "@/utils/lib";
import { getUserName } from "@/utils/usernames";

type CoinInfoWidgetProps = {
  fallback?: ReactNode;
};

const NOT_FOUND_LABEL = "N/A";

export const CoinInfoWidget = ({ fallback = null }: CoinInfoWidgetProps) => {
  const { loading: loadingExchangeRate, focusExchangeRate } =
    useFocusMarketData();

  const [showFounderCoins, setShowFounderCoins] = useState(false);
  const [showTotalCoins, setShowTotalCoins] = useState(false);
  const [showAmmConfig, setShowAmmConfig] = useState(false);
  const [refetching, setRefetching] = useState(false);

  const { userId } = useParams<{
    userId: string;
  }>();

  const {
    username: usernameHandle,
    publicKey,
    loading: usernameLoading,
  } = useResolveUsername(userId ?? "");
  const { currentUserPublicKey } = use(UserContext);
  const { data, isLoading } = useCoinPropertiesQuery({ publicKey });

  const {
    data: tokenMarketData,
    loading: tokenMarketDataLoading,
    refetch: refetchTokenMarketData,
  } = useTokenMarketData({
    publicKey,
    ammPublicKey: data?.coinProperties?.AmmConfigs?.[0]?.AmmPublicKey,
    currentUserPublicKey,
    enabled: Boolean(data?.coinProperties?.AmmConfigs?.[0]?.AmmPublicKey),
  });

  useEffect(() => {
    if (!data?.hasCoin || !data.coinProperties.RunAt[0]) return;

    const checkMarketOpen = async (intervalId?: number) => {
      const target = dayjs(data.coinProperties.RunAt[0]);
      const diffInSeconds = target.diff(dayjs(), "second");

      if (diffInSeconds <= 0) {
        if (!refetching) {
          setRefetching(true);
          if (intervalId) {
            clearInterval(intervalId);
          }
          await refetchTokenMarketData();
        }
      }
    };

    checkMarketOpen();

    const intervalId = window.setInterval(() => {
      checkMarketOpen(intervalId);
    }, 1000);

    return () => clearInterval(intervalId);
  }, [data, refetchTokenMarketData, refetching]);

  const finalLoading =
    isLoading ||
    tokenMarketDataLoading ||
    loadingExchangeRate ||
    usernameLoading;

  // Same height as the discover widget
  if (!data && (finalLoading || !focusExchangeRate))
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-[285px]" />
        <Skeleton className="h-[140px]" />
        <Skeleton className="h-[380px]" />
      </div>
    );

  if ((data && finalLoading) || !usernameHandle) {
    return fallback;
  }

  const coinProperties = data?.coinProperties;
  const price = data?.coinPrice;
  const hasCoin = data?.hasCoin;

  if (!isLoading && (!hasCoin || !data.hasAmmConfigs)) return fallback;

  const visiblePrice = price ? getVisiblePrice(price) : 0;
  const tokenSummary = tokenMarketData?.tokenMarketVolumeSummaries?.nodes[0];
  const tokenFounderSummary =
    tokenSummary?.coin1Account?.founderBalance.nodes[0];
  const currentUserBalance =
    tokenSummary?.coin1Account?.userBalance.nodes[0]?.totalBalance ?? 0;

  const ammConfig = coinProperties?.AmmConfigs?.length
    ? coinProperties?.AmmConfigs[0]
    : null;
  const marketCapField = Number(tokenSummary?.marketCapUsdCents ?? "0");
  const balanceField = tokenNanosToInteger(currentUserBalance);
  const totalSupplyNanos = Number(tokenSummary?.totalSupplyNanos ?? "0");
  const ownershipField =
    (tokenNanosToInteger(currentUserBalance) /
      tokenNanosToInteger(totalSupplyNanos)) *
    100;
  const totalHoldersField = tokenSummary?.hodlerCount || 0;
  const unlockedFounderTokenNanos = Number(
    tokenFounderSummary?.unlockedBalanceNanos ?? "0",
  );
  const unlockedFounderTokenField = tokenNanosToInteger(
    unlockedFounderTokenNanos,
  );
  const unlockFounderTokenSupplyField =
    (unlockedFounderTokenNanos * 100) / totalSupplyNanos;
  const lockedFounderTokenNanos = Number(
    tokenFounderSummary?.lockedBalanceNanos ?? "0",
  );
  const lockedFounderTokenField = tokenNanosToInteger(lockedFounderTokenNanos);
  const tokenFounderSummaryTotalBalance = Number(
    tokenFounderSummary?.totalBalance ?? "0",
  );
  const lockFounderTokenSupplyField =
    (lockedFounderTokenNanos * 100) / totalSupplyNanos;
  const nextFounderUnlockDateField = dateNanosToInteger(
    Number(
      tokenSummary?.coin1Account?.nextFounderUnlock?.nodes[0]
        ?.unlockTimestampNanoSecs ?? "0",
    ),
  );
  const totalFounderTokensField = tokenNanosToInteger(
    tokenFounderSummaryTotalBalance,
  );
  const totalFounderTokensSupplyField =
    (tokenFounderSummaryTotalBalance * 100) / totalSupplyNanos;
  const unlockedTokensNanos =
    Number(tokenSummary?.totalSupplyUnlockedNanos ?? "0") -
    Number(
      tokenSummary?.coin1Account?.ammBalance?.nodes[0]?.unlockedBalanceNanos ??
        0,
    );
  const unlockedTokensField = tokenNanosToInteger(unlockedTokensNanos);
  const unlockedTokensSupplyField =
    (unlockedTokensNanos * 100) / totalSupplyNanos;
  const lockedTokensNanos =
    Number(tokenSummary?.totalSupplyLockedNanos ?? "0") -
    Number(
      tokenSummary?.coin1Account?.ammBalance?.nodes[0]?.lockedBalanceNanos ??
        "0",
    );
  const lockedTokensField = tokenNanosToInteger(lockedTokensNanos);
  const lockedTokensSupplyField = (lockedTokensNanos * 100) / totalSupplyNanos;
  const nextUnlockDateField = dateNanosToInteger(
    Number(
      tokenSummary?.coin1Account?.nextUnlock?.nodes[0]
        ?.unlockTimestampNanoSecs ?? "0",
    ),
  );
  const tokensInAmmNanos = Number(
    tokenSummary?.coin1Account?.ammBalance?.nodes[0]?.totalBalance ?? "0",
  );
  const tokensInAmmField = tokenNanosToInteger(tokensInAmmNanos);
  const tokensInAmmSupplyField = (tokensInAmmNanos * 100) / totalSupplyNanos;

  const username = getUserName({
    account: {
      publicKey: coinProperties?.Profile.PublicKeyBase58Check,
      username: coinProperties?.Profile.Username,
      extraData: coinProperties?.Profile.ExtraData,
    },
    customPrefix: "$",
  });

  const coinPublicKey = coinProperties?.Profile.PublicKeyBase58Check ?? "";

  return (
    <div className="mb-12 flex flex-col gap-4">
      <div className="grid w-full items-start rounded-xl border bg-accent">
        <div className="flex flex-col items-start text-left text-xs">
          <PreviewRowItem label="Name" value={username} />
          <PreviewRowItem
            label="Address"
            value={
              <CopyToClipboard
                text={coinPublicKey}
                iconPosition="right"
                showToast
                toastText="Address copied to clipboard!"
              >
                {shortenLongWord(coinPublicKey, 8, 8)}
              </CopyToClipboard>
            }
          />
          <PreviewRowItem
            label="Category"
            value={
              coinProperties?.Profile?.ExtraData?.CoinCategoryExtraDataKey ||
              NOT_FOUND_LABEL
            }
          />
          {data?.ammRunning && (
            <>
              <PreviewRowItem
                label="Price"
                value={
                  visiblePrice ? (
                    <CoinPriceFormatter
                      price={visiblePrice}
                      highPriceThreshold={Infinity}
                    />
                  ) : (
                    NOT_FOUND_LABEL
                  )
                }
              />
              {publicKey === process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY ? (
                <>
                  {/* Special MC treatment for FOCUS token */}
                  <PreviewRowItem
                    label="Circulating Market Cap"
                    value={
                      price && getVisiblePrice(price) > 0
                        ? formatUSD(getVisiblePrice(price) * FOCUS_TOKEN_SUPPLY)
                        : NOT_FOUND_LABEL
                    }
                    tooltipText={`This is the total number of UNLOCKED coins, times the market price.`}
                  />
                  <PreviewRowItem
                    label="Fully Diluted Market Cap"
                    value={
                      marketCapField
                        ? formatUSDCents(Number(marketCapField))
                        : NOT_FOUND_LABEL
                    }
                    tooltipText={`This is the total number of coins, including locked coins, times the market price.`}
                  />
                </>
              ) : (
                <PreviewRowItem
                  label="Market Cap"
                  value={
                    marketCapField
                      ? formatUSDCents(marketCapField)
                      : NOT_FOUND_LABEL
                  }
                  tooltipText={`Market Caps shown in ${APP_NAME_GENERIC} are computed by multiplying the total number of tokens by the price.`}
                />
              )}
              <PreviewRowItem
                label="Your Balance"
                value={
                  <>
                    <div>
                      ~
                      <CoinPriceFormatter
                        price={balanceField * visiblePrice || 0}
                        highPriceThreshold={10}
                      />
                    </div>
                    <div
                      className="
                        inline-block text-muted
                        hover:text-foreground
                      "
                    >
                      <CoinPriceFormatter
                        price={usdToFocus(
                          balanceField * visiblePrice || 0,
                          focusExchangeRate ?? 0,
                        )}
                        className="text-xs"
                        highPriceThreshold={10}
                        isUsd={false}
                      />{" "}
                      {FOCUS_TOKEN_DISPLAY_NAME}
                    </div>
                  </>
                }
              />
              <PreviewRowItem
                label="Your Ownership"
                value={
                  ownershipField
                    ? `${formatDecimalValue(ownershipField)}%`
                    : "0%"
                }
              />
              <PreviewRowItem
                label="Total Holders"
                value={formatDecimalValue(totalHoldersField)}
              />
            </>
          )}
        </div>
      </div>

      <>
        <h2 className="flex items-center gap-2 text-xs font-semibold text-muted">
          Supply Summary{" "}
          <InfoTooltip
            iconSize={14}
            text={
              <>
                <p className="mb-3">
                  Tokens can either be unlocked, locked, or held by the AMM.
                  Since the AMM holds coins solely to provide liquidity, AMM
                  coins are considered separately from other tokens.
                </p>
              </>
            }
          />
        </h2>
        <div className="grid w-full items-start rounded-xl border bg-accent">
          <div className="flex flex-col items-start text-left text-xs">
            <>
              <div className="flex w-full items-center justify-between p-3 pb-0">
                <h2 className="flex items-center gap-2 text-xs font-semibold">
                  Founder Supply{" "}
                  <InfoTooltip
                    iconSize={14}
                    text={
                      <p>
                        This is the number of tokens held by the founder who
                        created this token.
                      </p>
                    }
                  />
                </h2>
                <div>
                  <button
                    onClick={() => setShowFounderCoins(!showFounderCoins)}
                    className="
                      text-xs text-muted underline underline-offset-4
                      hover:text-muted-foreground
                    "
                  >
                    {showFounderCoins ? "Hide" : "Details"}
                  </button>
                </div>
              </div>
              {showFounderCoins && (
                <>
                  <PreviewRowItem
                    label="Unlocked Founder Tokens"
                    value={formatDecimalValue(unlockedFounderTokenField)}
                    valueCaption={`${formatDecimalValue(unlockFounderTokenSupplyField)}%`}
                    tooltipText="Unlocked founder coins are the amount of $FOCUS tokens that have been unlocked from the founder supply."
                  />
                  <PreviewRowItem
                    label="Locked Founder Tokens"
                    value={formatDecimalValue(lockedFounderTokenField)}
                    valueCaption={`${formatDecimalValue(lockFounderTokenSupplyField)}%`}
                  />

                  {lockedFounderTokenField > 0 && (
                    <PreviewRowItem
                      label="Next Unlock Date"
                      value={
                        <TimestampTooltip
                          timestamp={new Date(
                            nextFounderUnlockDateField,
                          ).toISOString()}
                        >
                          <span>
                            {formatDateTime(nextFounderUnlockDateField, {
                              hour: undefined,
                              minute: undefined,
                            })}
                          </span>
                        </TimestampTooltip>
                      }
                    />
                  )}
                </>
              )}
              <PreviewRowItem
                label="Total Founder Tokens"
                value={formatDecimalValue(totalFounderTokensField)}
                valueCaption={`${formatDecimalValue(totalFounderTokensSupplyField)}%`}
              />
            </>
            {totalSupplyNanos > 0 && (
              <>
                <div
                  className="
                  flex w-full items-center justify-between p-3 pb-0
                "
                >
                  <h2 className="flex items-center gap-2 text-xs font-semibold">
                    Total Supply{" "}
                    <InfoTooltip
                      iconSize={14}
                      text={
                        <p>
                          This is the total number of tokens that currently
                          exist, including tokens that are held by the AMM to
                          provide liquidity.
                        </p>
                      }
                    />
                  </h2>
                  <div>
                    <button
                      onClick={() => setShowTotalCoins(!showTotalCoins)}
                      className="
                        text-xs text-muted underline underline-offset-4
                      "
                    >
                      {showTotalCoins ? "Hide" : "Details"}
                    </button>
                  </div>
                </div>
                {showTotalCoins && (
                  <>
                    <PreviewRowItem
                      label="Unlocked Tokens"
                      value={formatDecimalValue(unlockedTokensField)}
                      valueCaption={`${formatDecimalValue(unlockedTokensSupplyField)}%`}
                    />
                    <PreviewRowItem
                      label="Locked Tokens"
                      value={formatDecimalValue(lockedTokensField)}
                      valueCaption={`${formatDecimalValue(lockedTokensSupplyField)}%`}
                    />
                    {lockedTokensField > 0 && (
                      <PreviewRowItem
                        label="Next Unlock Date"
                        value={
                          <TimestampTooltip
                            timestamp={new Date(
                              nextUnlockDateField,
                            ).toISOString()}
                          >
                            <span>
                              {formatDateTime(nextUnlockDateField, {
                                hour: undefined,
                                minute: undefined,
                              })}
                            </span>
                          </TimestampTooltip>
                        }
                      />
                    )}
                    <PreviewRowItem
                      label="Tokens in AMM"
                      value={formatDecimalValue(tokensInAmmField)}
                      valueCaption={`${formatDecimalValue(tokensInAmmSupplyField)}%`}
                    />
                  </>
                )}
                <PreviewRowItem
                  label="Total Tokens"
                  value={formatDecimalValue(
                    tokenNanosToInteger(totalSupplyNanos),
                  )}
                />
              </>
            )}
          </div>
        </div>
      </>

      <h2 className="flex items-center gap-2 text-xs font-semibold text-muted">
        Token Properties{" "}
        <InfoTooltip
          iconSize={14}
          text={
            <>
              The creator of a token can adjust various properties, and even
              mint more coins. The safest coins have updates fully disabled on
              all properties, making them virtually &#34;un-ruggable.&#34;
            </>
          }
        />
      </h2>
      <div className="grid w-full items-start rounded-xl border bg-accent">
        <div className="flex flex-col items-start text-left text-xs">
          <div className="flex w-full items-center justify-between p-3 pb-0">
            <h2 className="text-xs font-semibold">Rewards</h2>
          </div>
          <PreviewRowItem
            label="Revenue Share"
            value={"Coming Soon!"}
            // value={
            //   formatDecimalValue(
            //     basisPointsToPercent(
            //       coinProperties?.CreatorRevsharePercentageBasisPoints ?? 0,
            //     ),
            //   ) + "%"
            // }
            tooltipText={`This is the percentage of a creators earnings that go to holders of their token. Every time a creator earns money on ${APP_NAME_GENERIC}, owners of their token can get a distribution so they share in the creator’s growth on the platform.`}
          />
          {!!coinProperties && (
            <TradingFeeRowItem
              publicKey={coinPublicKey}
              username={username}
              coinProperties={coinProperties}
            />
          )}

          <div className="flex w-full items-center justify-between p-3 pb-0">
            <h2 className="text-xs font-semibold">Yield Curve</h2>
          </div>
          <PreviewRowItem
            label="APY"
            value={
              formatDecimalValue(
                basisPointsToPercent(coinProperties?.CoinApyBasisPoints ?? 0),
              ) + "%"
            }
            tooltipText="Holders of a token can earn a yield for locking up their token for a certain amount of time. This can create more demand for a token. The APY is the “Annual Percentage Yield” for this particular token."
          />
          {(coinProperties?.CoinApyBasisPoints ?? 0) > 0 && (
            <PreviewRowItem
              label="Minimum Lockup Duration for Yield"
              value={pluralize(
                +FocusDateParser.fromNanoSeconds(
                  coinProperties?.MinLockupDurationNanos ?? 0,
                  "years",
                ),
                "year",
              )}
            />
          )}
          {data?.hasAmmConfigs && (
            <>
              <PreviewRowItem
                label="Updating Trading Fees"
                tooltipText="When trading fee updates are disabled, this gives traders the certainty that the fee will never change suddenly in the future."
                value={
                  coinProperties?.DisableTradingFeeUpdate
                    ? "Disabled"
                    : "Enabled"
                }
              />
              <PreviewRowItem
                label="Updating Revenue Share"
                tooltipText="When revenue share updates are disabled, this gives traders the certainty that the revenue share percentage will never decrease in the future."
                value={
                  coinProperties?.DisableCreatorRevshareUpdate
                    ? "Disabled"
                    : "Enabled"
                }
              />
            </>
          )}
          <PreviewRowItem
            label="Minting New Tokens"
            tooltipText="When token minting is disabled, it gives traders certainty that nobody will ever be able to mint more tokens in the future, thus guaranteeing a permanently fixed supply of the token. Beware of tokens that haven&#39;t disabled this property."
            value={
              coinProperties?.Profile.DAOCoinEntry?.MintingDisabled
                ? "Disabled"
                : "Enabled"
            }
          />
          <PreviewRowItem
            label="Updating Transfer Status"
            tooltipText={`${DESO_APP_NAME} Tokens come with the option to restrict transfers in various ways. Disable transfer restriction updates gives traders certainty around their ability to send their token to others.`}
            value={
              coinProperties?.Profile.DAOCoinEntry
                ?.TransferRestrictionStatus === "unrestricted"
                ? "Enabled"
                : "Disabled"
            }
          />
        </div>
      </div>

      {data?.hasAmmConfigs && ammConfig && (
        <>
          <h2 className="text-xs font-semibold text-muted">AMM Summary</h2>
          <div className="grid w-full items-start rounded-xl border bg-accent">
            <div className="flex flex-col items-start text-left text-xs">
              <div className="flex w-full items-center justify-between p-3 pb-0">
                <h2 className="text-xs font-semibold">AMM Stats</h2>
              </div>
              <PreviewRowItem
                label="USD in AMM Bids"
                value={formatUSD(coinProperties?.UsdInAmmBidsTotal ?? 0)}
                tooltipText="This is the amount of USD that the AMM is offering on the buy side of this token's market. A higher number here generally implies that the AMM can absorb more selling pressure without the price going down as much."
              />
              <PreviewRowItem
                label="Tokens in AMM Asks"
                value={formatDecimalValue(
                  coinProperties?.CoinsInAmmAsksTotal ?? 0,
                )}
                tooltipText="This is the USD it would take to buy all of the tokens currently being used to provide liquidity by the AMM. A high value here could imply that the AMM is putting a lot of sell pressure on the market, but it could also just be that the AMM has a lot of orders placed at very high prices. You would need to look at the spacing of the AMM's orders to know for sure."
              />
              <div className="flex w-full items-center justify-between p-3">
                <h2 className="text-xs font-semibold">AMM Config</h2>
                <div>
                  <button
                    onClick={() => setShowAmmConfig(!showAmmConfig)}
                    className="
                      text-xs text-muted underline underline-offset-4
                      hover:text-muted-foreground
                    "
                  >
                    {showAmmConfig ? "Hide" : "Details"}
                  </button>
                </div>
              </div>

              {showAmmConfig && (
                <>
                  <PreviewRowItem
                    label="Start Price USD"
                    value={
                      <CoinPriceFormatter price={ammConfig.StartPriceUsd} />
                    }
                  />
                  <PreviewRowItem
                    label="Order Spacing"
                    value={
                      formatDecimalValue(
                        basisPointsToPercent(ammConfig.OrderSpacingBasisPoints),
                      ) + "%"
                    }
                  />
                  <PreviewRowItem
                    label="Amount Per Level USD"
                    value={
                      <CoinPriceFormatter
                        price={ammConfig.BaseAmountPerLevelUsd}
                      />
                    }
                  />

                  {ammConfig.IncreaseBaseAmountByUsdEachLevel > 0 ? (
                    <PreviewRowItem
                      label="Amount Increase by USD"
                      value={
                        <CoinPriceFormatter
                          price={ammConfig.IncreaseBaseAmountByUsdEachLevel}
                          maxDecimalsDigitsAfterZero={4}
                        />
                      }
                    />
                  ) : (
                    <PreviewRowItem
                      label="Amount Increase Per Level"
                      value={
                        formatDecimalValue(
                          basisPointsToPercent(
                            ammConfig.IncreaseBaseAmountByBasisPointsEachLevel,
                          ),
                        ) + "%"
                      }
                    />
                  )}

                  <PreviewRowItem
                    label="Terminal Price USD"
                    value={
                      <CoinPriceFormatter price={ammConfig.TerminalPriceUsd} />
                    }
                  />
                  <PreviewRowItem
                    label="Terminal Amount USD"
                    value={
                      <CoinPriceFormatter price={ammConfig.TerminalAmountUsd} />
                    }
                  />
                  <PreviewRowItem
                    label="Final Price USD"
                    value={
                      <CoinPriceFormatter price={ammConfig.FinalPriceUsd} />
                    }
                  />
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

type PreviewRowItemProps = {
  label: ReactNode;
  value: ReactNode;
  className?: string;
  labelClassName?: string;
  valueCaption?: string;
  tooltipText?: string | ReactNode;
};

const TradingFeeRowItem = ({
  publicKey,
  username,
  coinProperties,
}: {
  publicKey: string;
  username: string;
  coinProperties: GetCoinPropertiesResponse;
}) => {
  if (!publicKey) {
    return null;
  }

  const { loading: loadingCreatorCoinFees, creatorCoinMarketFee } =
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useCreatorCoinFee({
      username,
      publicKey,
    });

  return (
    <PreviewRowItem
      label="Trading Fees"
      value={
        formatDecimalValue(
          basisPointsToPercent(coinProperties.TotalTradingFeeBasisPoints || 0),
        ) + "%"
      }
      tooltipText={
        <PricePerCoinTooltip
          account={{ username, publicKey }}
          loading={loadingCreatorCoinFees}
          creatorCoinMarketFee={creatorCoinMarketFee}
        />
      }
    />
  );
};

export const PreviewRowItem = ({
  label,
  valueCaption,
  className = "",
  labelClassName = "",
  value,
  tooltipText = "",
}: PreviewRowItemProps) => {
  return (
    <div
      className={cn(
        `
          unforce-radius flex w-full items-start justify-between border-b
          border-border-light px-3 py-2
          last:border-none
        `,
        className,
      )}
    >
      <div className="flex w-full max-w-[65%] flex-row items-end gap-2">
        <div className="flex items-start gap-1 text-xs text-muted">
          <span className={cn(labelClassName)}>{label}</span>
          {tooltipText && (
            <InfoTooltip
              iconSize={14}
              iconClassName="ml-1 mt-0.5"
              text={tooltipText}
            />
          )}
        </div>
      </div>
      <div
        className="
        flex flex-col items-end text-right text-xs text-foreground
      "
      >
        <div
          className="
            inline-block text-muted
            hover:text-foreground
          "
        >
          {value == "Disabled" && (
            <>
              <LuCheck className="mr-1 inline-flex text-base text-green-500" />
              <div className="inline-block font-mono text-xs text-green-500">
                {value}
              </div>
            </>
          )}
          {value == "Enabled" && (
            <>
              <LuX className="mr-1 inline-flex text-base text-red-500" />
              <div className="inline-block font-mono text-xs text-red-500">
                {value}
              </div>
            </>
          )}
          {value != "Disabled" && value != "Enabled" && (
            <div className="inline-block font-mono text-muted-foreground">
              {value}
            </div>
          )}{" "}
        </div>
        {valueCaption && (
          <div>
            <span
              className="
                inline-block text-muted
                hover:text-foreground
              "
            >
              {valueCaption}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
